import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import LabeledInput, { PhoneInput } from './components/LabeledInput';
import type { SupportedFormData } from './schemas/supportedFormSchema';
import { toHebrewName } from '@/helpers/hebrewName';
import { SUPPORTED_PERSON_AUTOCOMPLETE, SUPPORTED_PERSON_FIELD_DOM } from './supportedFormAutofill';

const SupportedPersonalInfo = () => {
  const { t } = useTranslation();
  const { control } = useFormContext<SupportedFormData>();
  const dom = SUPPORTED_PERSON_FIELD_DOM;
  const ac = SUPPORTED_PERSON_AUTOCOMPLETE;

  return (
    <div className="flex flex-col gap-8 w-[850px] max-md:w-full" data-testid="supported-personal-info">
      <div className="flex flex-col gap-4">
        <h4 className="body-lr-semibold text-(--font-sub-title-2)">{t('supportedPersonalInfo')}</h4>
        <div className="flex gap-8 max-md:flex-col max-md:gap-4">
          <Controller
            name="contact.firstName"
            control={control}
            render={({ field }) => (
              <LabeledInput
                label={t('firstName')}
                id={dom.firstName.id}
                name={dom.firstName.name}
                autoComplete={ac}
                width="410px"
                mobileFullWidth
                value={field.value}
                onChange={(e) => field.onChange(toHebrewName(e.target.value))}
                required
              />
            )}
          />
          <Controller
            name="contact.lastName"
            control={control}
            render={({ field }) => (
              <LabeledInput
                label={t('lastName')}
                id={dom.lastName.id}
                name={dom.lastName.name}
                autoComplete={ac}
                width="410px"
                mobileFullWidth
                value={field.value}
                onChange={(e) => field.onChange(toHebrewName(e.target.value))}
                required
              />
            )}
          />
        </div>
        <div className="flex gap-8 max-md:flex-col max-md:gap-4">
          <Controller
            name="contact.address.city"
            control={control}
            render={({ field }) => (
              <LabeledInput
                label={t('city')}
                id={dom.city.id}
                name={dom.city.name}
                autoComplete={ac}
                width="240px"
                mobileFullWidth
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value)}
                required
              />
            )}
          />
          <Controller
            name="contact.address.streetName"
            control={control}
            render={({ field }) => (
              <LabeledInput
                label={t('streetName')}
                id={dom.streetName.id}
                name={dom.streetName.name}
                autoComplete={ac}
                width="260px"
                mobileFullWidth
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value)}
                required={false}
              />
            )}
          />
          <Controller
            name="contact.address.streetNumber"
            control={control}
            render={({ field }) => (
              <LabeledInput
                label={t('houseNumber')}
                id={dom.streetNumber.id}
                name={dom.streetNumber.name}
                autoComplete={ac}
                width="140px"
                mobileFullWidth
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value)}
                required={false}
              />
            )}
          />
          <Controller
            name="contact.address.zipCode"
            control={control}
            render={({ field }) => (
              <LabeledInput
                label={t('zipCode')}
                id={dom.zipCode.id}
                name={dom.zipCode.name}
                autoComplete={ac}
                width="116px"
                mobileFullWidth
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value)}
                required={false}
              />
            )}
          />
        </div>
      </div>

      <div className="w-full h-0 border-t border-dashed border-(--border-lowest)" />

      <div className="flex flex-col gap-4">
        <h4 className="body-lr-semibold text-(--font-sub-title-2)">{t('contactInfo')}</h4>
        <div className="flex gap-8 max-md:flex-col max-md:gap-4">
          <Controller
            name="contact.phone"
            control={control}
            render={({ field }) => (
              <PhoneInput
                isMobile
                label={`${t('phoneNumber')} ${t('mobile')}`}
                id={dom.phone.id}
                name={dom.phone.name}
                autoComplete={ac}
                numberInputType="text"
                width="262.67px"
                mobileFullWidth
                value={field.value || ''}
                onChange={(val) => field.onChange(val)}
                required={false}
              />
            )}
          />
          {/* TODO: Uncomment when adding phone of home to the Conact in table */}
          {/* <Controller
            name="contact.homePhone"
            control={control}
            render={({ field }) => (
              <PhoneInput
                label={`${t('phoneNumber')} ${t('home')}`}
                id="homePhone"
                width="262.67px"
                value={field.value || ''}
                onChange={(val) => field.onChange(val)}
                required={false}
              />
            )}
          /> */}
          <Controller
            name="contact.email"
            control={control}
            render={({ field }) => (
              <LabeledInput
                label={t('email')}
                id={dom.email.id}
                name={dom.email.name}
                type="text"
                inputMode="email"
                autoComplete={ac}
                width="262.67px"
                mobileFullWidth
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value)}
                required={false}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default SupportedPersonalInfo;
